import { Disclosure, Transition } from '@headlessui/react';
import dayjs, { Dayjs } from 'dayjs';
import { cloneDeep } from 'lodash';
import capitalize from 'lodash/capitalize';
import lowerCase from 'lodash/lowerCase';
import omit from 'lodash/omit';
import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import RegisterClassesSelectPaginate, {
  ClassItemOption,
} from '@/modules/common/components/RegisterClassesSelectPaginate.tsx';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { activityFilters } from '@/modules/investor-dashboard/pages/InvestorActivity.tsx';
import { CalendarInput, RangeNumberInput, Select, SelectOption, SelectPaginateOption } from 'ui';
import { fixNameEnum, getDisabledDate } from 'ui/utils';
import { ActivitiesStatus, ActivitiesType } from 'api';

const activityStatusOptions: SelectOption<ActivitiesStatus>[] = Object.values(ActivitiesStatus).map(
  status => ({
    label: capitalize(lowerCase(status)),
    value: status,
  }),
);

interface InvestorActivityFiltersProps {
  showStatus?: boolean;
  types?: ActivitiesType[];
  filters: activityFilters;
  setFilters: (cb: (filters: activityFilters) => activityFilters) => void;
}

const InvestorActivityFilters = ({
  types,
  filters,
  showStatus,
  setFilters,
}: InvestorActivityFiltersProps) => {
  const [params] = useSearchParams();
  const registerId = +(params.get(REGISTER_ID_SEARCH_PARAM_KEY) || '');

  const getRegisterTypeStrings = useRegisterTypeStrings();

  const {
    type,
    status,
    total_from,
    total_to,
    requested_at_date_from,
    requested_at_date_to,
    completion_date_from,
    completion_date_to,
  } = filters;

  const handleChangeType = useCallback(
    (type: ActivitiesType | null) => {
      setFilters(filters => (type ? { ...filters, type } : omit(filters, 'type')));
    },
    [setFilters],
  );

  const handleChangeStatus = useCallback(
    (status: ActivitiesStatus | null) => {
      if (status) {
        setFilters(filters => ({ ...filters, status }));
      } else {
        setFilters(filters => omit(filters, 'status'));
      }
    },
    [setFilters],
  );

  const handleChangeRequestedDate = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null) => {
      setFilters(filters =>
        dates && dates[0] && dates[1]
          ? {
              ...filters,
              requested_at_date_from: dates[0].toISOString(),
              requested_at_date_to: dates[1].endOf('d').toISOString(),
            }
          : omit(filters, ['requested_at_date_from', 'requested_at_date_to']),
      );
    },
    [setFilters],
  );

  const handleChangeCompletionDate = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null) => {
      setFilters(filters =>
        dates && dates[0] && dates[1]
          ? {
              ...filters,
              completion_date_from: dates[0].toISOString(),
              completion_date_to: dates[1].endOf('d').toISOString(),
            }
          : omit(filters, ['completion_date_from', 'completion_date_to']),
      );
    },
    [setFilters],
  );

  const handleChangeTotal = useCallback(
    (value: Array<string | undefined>) => {
      setFilters(filters => {
        let res = cloneDeep(filters);

        if (value[0]) {
          res.total_from = +value[0];
        } else {
          res = omit(res, ['total_from']);
        }

        if (value[1]) {
          res.total_to = +value[1];
        } else {
          res = omit(res, ['total_to']);
        }

        return res;
      });
    },
    [setFilters],
  );

  const handleChangeRegisterClass = useCallback(
    (options: SelectPaginateOption<ClassItemOption> | null) => {
      if (options) {
        setFilters(filters => ({ ...filters, register_class_id: options.value?.id }));
      } else {
        setFilters(filters => omit(filters, ['register_class_id']));
      }
    },
    [setFilters],
  );

  const typeOptions = useMemo(() => {
    return (types || Object.values(ActivitiesType)).map(value => {
      if (value === ActivitiesType.Drp) {
        return {
          label: 'Dividend reinvestment',
          value,
        };
      }

      if (value === ActivitiesType.Subscription) {
        return {
          label: getRegisterTypeStrings('application', { capitalize: true }),
          value,
        };
      }

      return {
        label: fixNameEnum(value),
        value,
      };
    });
  }, [getRegisterTypeStrings, types]);

  useEffect(() => {
    if (!registerId && filters.register_class_id) {
      setFilters(filters => omit(filters, ['register_class_id']));
    }
  }, [filters.register_class_id, registerId, setFilters]);
  return (
    <Transition
      enter="transition duration-100 ease-out"
      enterFrom="transform scale-95 opacity-0"
      enterTo="transform scale-100 opacity-100"
      leave="transition duration-75 ease-out"
      leaveFrom="transform scale-100 opacity-100"
      leaveTo="transform scale-95 opacity-0"
    >
      <Disclosure.Panel static>
        <div className="w-full flex flex-col items-start">
          <div className="w-full flex items-end gap-4 flex-wrap [&>*]:min-w-[230px] [&>*]:w-fit [&>*]:max-w-[300px] [&>*]:flex-1">
            <CalendarInput
              range
              disabledDate={getDisabledDate}
              label="request date"
              className="[&>div]:!py-3 [&>label]:!m-0"
              name="requested_at_date"
              placeholder="Any date"
              rangeValue={
                requested_at_date_from && requested_at_date_to
                  ? [dayjs(requested_at_date_from), dayjs(requested_at_date_to)]
                  : null
              }
              rangeOnChange={handleChangeRequestedDate}
            />
            <CalendarInput
              range
              disabledDate={getDisabledDate}
              label="completion date"
              className="[&>div]:!py-3 [&>label]:!m-0"
              name="completion_date"
              placeholder="Any date"
              rangeValue={
                completion_date_from && completion_date_to
                  ? [dayjs(completion_date_from), dayjs(completion_date_to)]
                  : null
              }
              rangeOnChange={handleChangeCompletionDate}
            />
            <Select
              isClearable
              label="type"
              name="type"
              placeholder="Any type"
              value={type}
              onChange={handleChangeType}
              options={typeOptions}
            />
            {showStatus && (
              <Select
                isClearable
                label="status"
                name="status"
                placeholder="Any status"
                value={status}
                onChange={handleChangeStatus}
                options={activityStatusOptions}
              />
            )}
            {!!registerId && (
              <RegisterClassesSelectPaginate
                clearable
                label="class"
                formFieldTitle=""
                isMulti={false}
                value={filters?.register_class_id}
                onChange={handleChangeRegisterClass}
              />
            )}
            <RangeNumberInput
              clearable
              label="total $"
              value={[total_from?.toString(), total_to?.toString()]}
              onChange={handleChangeTotal}
            />
          </div>
        </div>
      </Disclosure.Panel>
    </Transition>
  );
};

export default InvestorActivityFilters;
